import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { BankDetail } from "./types";
import AddInputs from "../../../../components/Add/AddInputs";
import S3ImageUpload from "../../../../components/S3uploads/S3ImageUpload";
import { useDispatch } from "react-redux";
import { setAddLoading, setBankDetails, setBankDetailsAdd, setBankDetailsRemove, setBankErrors, setEditBankDetails, setSavedData } from "../addMDSlice";
import Image from "../../../../components/images/Image";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import useApi from "../../../../services/useApi";
import { showToast } from "../../../../components/toastMessage/toastSlice";

// Define the type for the exposed functions
export interface bankDetailsRef {
  handleSubmit: () => void;
}

interface bankDetailsProps {
  handleNext: () => void;
  id: string;
  type: string;
  firstIndex?: boolean;
}
const BankDetails = forwardRef<bankDetailsRef, bankDetailsProps>(({ handleNext, id, type , firstIndex}, ref) => {
  //states and varable used in the feature
  const dispatch = useDispatch();
  const { bankDetails, savedData, bankDetailsErrors, user_id } = useSelector((state: RootState) => state.addMD);
  const bankDetailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { PatchAPI } = useApi();
  const getUrlPath = (type: any) => type?.toLowerCase().split(' ').join('-');

  //for bank details
  const bankDetailsInputs = {
    "": {
      "Bank Name": {
        required: true,
        disabled: false,
        id: "bank_name",
        type: "input",
        tabIndex: 0,
        maxLength: 60,
      },
      "Account Number": {
        required: true,
        disabled: false,
        id: "account_number",
        type: "input",
        tabIndex: 2,
        maxLength: 60,
      }
    },
  }

  //input handler
  const handleInputChanges = (key: string, value: string, index: number) => {
    // Update the bankDetails state with the new value
    dispatch(setBankDetails({ key, value, index }));
  };

  //validation for empty fields
  const validateBankDetails = () => {
    // Initialize bankDetailsErrors with the same structure as bankDetails
    const bankDetailsErrors = bankDetails.map(() => ({
      bank_name: '',
      account_number: '',
      bank_passbook_front_page: '',
    }));

    let hasErrors = false; // Flag to track if there are any errors

    // Iterate through bankDetails and validate each field
    bankDetails.forEach((detail: BankDetail, index: number) => {
      if (!detail.bank_name) {
        bankDetailsErrors[index].bank_name = 'This field is required';
        hasErrors = true; // Set flag to true if there's an error
      }
      if (!detail.account_number) {
        bankDetailsErrors[index].account_number = 'This field is required';
        hasErrors = true; // Set flag to true if there's an error
      }
      if (!detail.bank_passbook_front_page) {
        bankDetailsErrors[index].bank_passbook_front_page = 'This field is required';
        hasErrors = true; // Set flag to true if there's an error
      }
    });
    return { bankDetailsErrors, hasErrors };
  };

  //submit handler function
  const handleSubmit = async () => {
    // Validate the bankDetails
    const { bankDetailsErrors, hasErrors } = validateBankDetails();

    // Check if there are any errors
    if (hasErrors) {
      dispatch(setBankErrors(bankDetailsErrors));
      // Find the first index with errors
      let firstErrorIndex = bankDetailsErrors.findIndex(errors =>
        errors.bank_name || errors.account_number || errors.bank_passbook_front_page
      );

      // Scroll to the first div with errors
      if (firstErrorIndex !== -1 && bankDetailRefs.current[firstErrorIndex]!== undefined && !firstIndex) {
        bankDetailRefs.current[firstErrorIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return; // Exit the function if there are errors
    }

    // If there are no errors, proceed with the next step of creating payload for the API
    let payload = {
      bank_details: bankDetails
    }
    dispatch(setAddLoading(true));
    let response;
    response = await PatchAPI(`${getUrlPath(type)}/${user_id}`, payload);
    if (!response.error) {
      dispatch(setSavedData({ key: 'bank details', value: payload.bank_details }));
      handleNext();
    } else {
      //error handling
      dispatch(
        showToast({
          type: "error",
          message: response?.data?.response?.data?.message || 'Something went wrong',
        })
      );
      console.error("Error:", response);
    }
    dispatch(setAddLoading(false));

  };  // Expose functions to parent
  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  //setting bank details when switching the tabs only duing edit mode
  useEffect(() => {
    if (id || user_id) {
      const savedBankDetails = savedData?.['bank details'] || [];
      dispatch(setEditBankDetails(savedBankDetails));
    }
  }, [])
  return (
    <div>
      {bankDetails.length > 0 && bankDetails.map((bank: BankDetail, index: number) => (
        <div ref={el => bankDetailRefs.current[index] = el} key={index} className="bg-background_blue p-4 rounded-[8px] mb-4">
          <AddInputs mapping={true} index={index} errors={bankDetailsErrors[index]} totalInputCount={2} onInputChange={(id, value) => handleInputChanges(id, value, index)} inputs={bankDetailsInputs} inputValues={bank} />
          <S3ImageUpload
            heading=""
            label="Bank Passbook Front Page"
            disabled={false}
            upload_id={'bank_passbook_front_page'}
            UploadPath={"uploads/bank_passbook_front_page"}
            handleUpload={(file, upload_id) => { handleInputChanges(file, upload_id, index) }}
            uploadedFile={bank?.bank_passbook_front_page}
            errorMessage={bankDetailsErrors[index]['bank_passbook_front_page']}
            required={true}
            id={`bank_passbook_front_page-${index}`}
          />
          {bankDetails.length > 1 && <div className="flex justify-end">
            <Image src={'delete_blue'} className="cursor-pointer" testId={`delete-icon-${index}`} onClick={() => {
              dispatch(setBankDetailsRemove(index))
            }} />
          </div>}
        </div>

      ))}
      {bankDetails.length < 5 && <div data-testid={'add-bank-sections'} className="w-fit flex items-center mt-2 cursor-pointer" onClick={() => dispatch(setBankDetailsAdd())}>        <Image src="plus" />
        <p className="text-p_primary text-body2">{`Add (You can add up to ${5 - bankDetails.length} bank details)`}</p>
      </div>}
    </div>
  )
});

export default BankDetails;